import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { DataContext } from '../contexts/DataProvider';

const usePatientsForDoctor = (doctorUid) => {
  const { fetchPatientsForDoctor, refreshPatientsData } = useContext(DataContext);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['patientsList', doctorUid],
    queryFn: async () => {
      if (!doctorUid) {
        throw new Error('Doctor UID is required');
      }
      return await fetchPatientsForDoctor(doctorUid);
    },
    enabled: !!doctorUid,
    staleTime: Infinity, // Data never becomes stale automatically - only manual invalidation
    gcTime: 10 * 60 * 1000, // 10 minutes cache time
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnReconnect: false, // Don't refetch on reconnect
    retry: 1, // Only retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
    // Prevent any automatic refetching
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  // Manual refresh function
  const refreshData = async () => {
    if (!doctorUid) return;
    
    // Invalidate the React Query cache
    await queryClient.invalidateQueries({ queryKey: ['patientsList', doctorUid] });
    
    // Also refresh the DataProvider cache
    await refreshPatientsData(doctorUid);
  };

  return {
    ...query,
    refreshData,
  };
};

export default usePatientsForDoctor;
